import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, Switch, StyleSheet, ActivityIndicator, Alert } from 'react-native';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import { getPreferences, setPreferences, type Preferences } from '../../features/preferences/preferencesStore';
import { scheduleDailyReminder, cancelDailyReminder } from '../../features/notifications/dailyReminder';
import { parseHourMinute, formatHourMinute } from '../shared/parseHourMinute';
import { colors, fonts, spacing } from '../theme';
import { Heading } from '../shared/Typography';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';

/**
 * Tela de Preferências (Etapa 14 — S01 §Preferências, S03 §Notificações):
 * liga os dois lembretes locais, manter tela ligada e reduzir brilho. Cada
 * switch persiste imediatamente via `setPreferences` (AsyncStorage) — não
 * há botão "Salvar" geral, só o próprio toggle. O lembrete de pausa/
 * keep-awake/brilho só têm EFEITO na próxima vez que a tela de Sessão ler a
 * preferência (não interrompe uma sessão já em andamento em outra tela); o
 * lembrete diário, por não depender da tela de Sessão, agenda/cancela na
 * hora.
 *
 * Idioma continua editável em Perfil > Meus dados (já implementado desde a
 * Etapa 9) — não duplicado aqui, só referenciado.
 */
export function PreferencesScreen() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [timeText, setTimeText] = useState('');
  const [savingTime, setSavingTime] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const loaded = await getPreferences();
      setPrefs(loaded);
      setTimeText(formatHourMinute({ hour: loaded.dailyReminderHour, minute: loaded.dailyReminderMinute }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleTogglePauseReminder = async (value: boolean) => {
    const updated = await setPreferences({ pauseReminderEnabled: value });
    setPrefs(updated);
  };

  const handleToggleKeepScreenOn = async (value: boolean) => {
    const updated = await setPreferences({ keepScreenOnEnabled: value });
    setPrefs(updated);
  };

  const handleToggleReduceBrightness = async (value: boolean) => {
    const updated = await setPreferences({ reduceBrightnessEnabled: value });
    setPrefs(updated);
  };

  const handleToggleDailyReminder = async (value: boolean) => {
    const updated = await setPreferences({ dailyReminderEnabled: value });
    setPrefs(updated);
    if (value) {
      await scheduleDailyReminder(updated.dailyReminderHour, updated.dailyReminderMinute);
    } else {
      await cancelDailyReminder();
    }
  };

  const handleSaveTime = async () => {
    const parsed = parseHourMinute(timeText);
    if (!parsed) {
      Alert.alert(t('preferences.title'), t('preferences.dailyReminderTimeInvalid'));
      return;
    }
    setSavingTime(true);
    try {
      const updated = await setPreferences({ dailyReminderHour: parsed.hour, dailyReminderMinute: parsed.minute });
      setPrefs(updated);
      if (updated.dailyReminderEnabled) {
        await scheduleDailyReminder(parsed.hour, parsed.minute);
      }
    } finally {
      setSavingTime(false);
    }
  };

  if (loading || !prefs) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={colors.sageDeep} />
        <Text style={styles.muted}>{t('preferences.loading')}</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Heading style={styles.title}>{t('preferences.title')}</Heading>

      <View style={styles.row}>
        <View style={styles.rowText}>
          <Text style={styles.label}>{t('preferences.pauseReminderLabel')}</Text>
          <Text style={styles.hint}>{t('preferences.pauseReminderHint')}</Text>
        </View>
        <Switch
          value={prefs.pauseReminderEnabled}
          onValueChange={handleTogglePauseReminder}
          trackColor={{ true: colors.sage, false: colors.line }}
          thumbColor={colors.sand}
        />
      </View>

      <View style={styles.row}>
        <View style={styles.rowText}>
          <Text style={styles.label}>{t('preferences.dailyReminderLabel')}</Text>
          <Text style={styles.hint}>{t('preferences.dailyReminderHint')}</Text>
        </View>
        <Switch
          value={prefs.dailyReminderEnabled}
          onValueChange={handleToggleDailyReminder}
          trackColor={{ true: colors.sage, false: colors.line }}
          thumbColor={colors.sand}
        />
      </View>

      {prefs.dailyReminderEnabled && (
        <View style={styles.timeRow}>
          <Text style={styles.label}>{t('preferences.dailyReminderTimeLabel')}</Text>
          <TextField
            style={styles.timeInput}
            placeholder={t('preferences.dailyReminderTimePlaceholder')}
            value={timeText}
            onChangeText={setTimeText}
            keyboardType="numbers-and-punctuation"
            editable={!savingTime}
          />
          <Button title={t('preferences.dailyReminderTimeSave')} variant="line" onPress={handleSaveTime} disabled={savingTime} />
        </View>
      )}

      <View style={styles.row}>
        <View style={styles.rowText}>
          <Text style={styles.label}>{t('preferences.keepScreenOnLabel')}</Text>
          <Text style={styles.hint}>{t('preferences.keepScreenOnHint')}</Text>
        </View>
        <Switch
          value={prefs.keepScreenOnEnabled}
          onValueChange={handleToggleKeepScreenOn}
          trackColor={{ true: colors.sage, false: colors.line }}
          thumbColor={colors.sand}
        />
      </View>

      <View style={styles.row}>
        <View style={styles.rowText}>
          <Text style={styles.label}>{t('preferences.reduceBrightnessLabel')}</Text>
          <Text style={styles.hint}>{t('preferences.reduceBrightnessHint')}</Text>
        </View>
        <Switch
          value={prefs.reduceBrightnessEnabled}
          onValueChange={handleToggleReduceBrightness}
          trackColor={{ true: colors.sage, false: colors.line }}
          thumbColor={colors.sand}
        />
      </View>

      <View style={styles.row}>
        <View style={styles.rowText}>
          <Text style={styles.label}>{t('preferences.languageLabel')}</Text>
          <Text style={styles.hint}>{t('preferences.languageHint')}</Text>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: spacing.md, backgroundColor: colors.sand },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.md, backgroundColor: colors.sand },
  muted: { fontFamily: fonts.body, color: colors.thread, marginTop: spacing.sm },
  title: { textAlign: 'left', marginTop: spacing.md, marginBottom: spacing.md },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm + 4,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
  },
  rowText: { flex: 1, marginRight: spacing.md },
  label: { fontFamily: fonts.bodyBold, fontSize: 15, color: colors.ink },
  hint: { fontFamily: fonts.body, fontSize: 12, color: colors.thread, marginTop: 2 },
  timeRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  timeInput: { width: 90, marginBottom: 0 },
});
